import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, Image, Linking } from 'react-native';
import { useNavigation, useRoute, RouteProp, CompositeNavigationProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import QuoteCard from '../../components/work/QuoteCard';
import EmptyState from '../../components/work/EmptyState';
import EndContractModal from '../../components/work/EndContractModal';
import { WorkStackParamList, RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../../components/common/ThemedText';
import { Job, Quote, ContractCompletionRequest } from '../../types/work';
import { showSuccessToast, showErrorToast, Alert } from '../../utils/toast';
import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';
import { shareItem } from '../../utils/share';

type ContractDetailNavigationProp = CompositeNavigationProp<
  StackNavigationProp<WorkStackParamList, 'ContractDetail'>,
  StackNavigationProp<RootStackParamList>
>;
type ContractDetailRouteProp = RouteProp<WorkStackParamList, 'ContractDetail'>;

const ContractDetailScreen: React.FC = () => {
  const navigation = useNavigation<ContractDetailNavigationProp>();
  const route = useRoute<ContractDetailRouteProp>();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { fetchJobById, fetchJobQuotes, fetchMyQuotes, myQuotes, acceptQuote, requestCompletion, confirmCompletion } = useWork();
  const { jobId } = route.params;
  
  const [job, setJob] = useState<Job | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [myQuote, setMyQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [activeTab, setActiveTab] = useState<'quotes' | 'details'>('quotes');
  
  // End Contract modal state
  const [endContractModalVisible, setEndContractModalVisible] = useState(false);
  const [endContractMode, setEndContractMode] = useState<'request' | 'respond'>('request');
  const [selectedQuoteForEndContract, setSelectedQuoteForEndContract] = useState<Quote | null>(null);
  const [pendingCompletionRequests, setPendingCompletionRequests] = useState<Record<string, ContractCompletionRequest>>({});

  useEffect(() => {
    loadJob();
  }, [jobId]);

  useEffect(() => {
    // Check if user already sent a quote for this job
    const existingQuote = myQuotes.find(q => q.jobId === jobId);
    setMyQuote(existingQuote || null);
    
    // Fetch completion request for my quote if it's in accepted status
    if (existingQuote && existingQuote.status === 'accepted') {
      fetchCompletionRequest(existingQuote.id);
    }
  }, [myQuotes, jobId]);

  const loadJob = async () => {
    setLoading(true);
    const jobData = await fetchJobById(jobId);
    setJob(jobData);
    
    // If user owns the job, fetch quotes
    if (jobData && jobData.createdBy.id === user?.userId) {
      setLoadingQuotes(true);
      const quotesData = await fetchJobQuotes(jobId);
      setQuotes(quotesData);
      
      // Fetch completion requests for all accepted quotes
      for (const quote of quotesData) {
        if (quote.status === 'accepted') {
          fetchCompletionRequest(quote.id);
        }
      }
      
      setLoadingQuotes(false);
    } else {
      // If user doesn't own the job, check if they already sent a quote
      await fetchMyQuotes();
    }
    
    setLoading(false);
  };
  
  const fetchCompletionRequest = async (quoteId: string) => {
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/completion-request`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success && data.completionRequest) {
        setPendingCompletionRequests(prev => ({
          ...prev,
          [quoteId]: data.completionRequest,
        }));
      } else {
        setPendingCompletionRequests(prev => {
          if (!prev[quoteId]) return prev;
          const updated = { ...prev };
          delete updated[quoteId];
          return updated;
        });
      }
    } catch (error) {
      console.error('Error fetching completion request:', error);
    }
  };
  
  const handleEndContractRequest = async (requestedStatus: 'completed' | 'partially_completed' | 'cancelled') => {
    if (!selectedQuoteForEndContract) return;
    
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${selectedQuoteForEndContract.id}/request-end-contract`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ requestedStatus }),
      });
      
      const data = await response.json();
      if (data.success) {
        showSuccessToast('End contract request sent successfully!');
        // Refresh completion request
        await fetchCompletionRequest(selectedQuoteForEndContract.id);
        // Reload quotes
        if (isMyJob) {
          const quotesData = await fetchJobQuotes(jobId);
          setQuotes(quotesData);
        } else {
          await fetchMyQuotes();
        }
      } else {
        throw new Error(data.message || 'Failed to send request');
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to send end contract request');
      throw error;
    }
  };
  
  const handleRespondToEndContract = async (action: 'accept' | 'reject') => {
    if (!selectedQuoteForEndContract) return;
    
    try {
      const token = await storage.getToken();
      const response = await fetch(buildApiUrl(`api/quotes/${selectedQuoteForEndContract.id}/respond-end-contract`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action }),
      });
      
      const data = await response.json();
      if (data.success) {
        if (action === 'accept') {
          showSuccessToast('Contract ended successfully!');
        } else {
          showSuccessToast('Request rejected. The other party can submit a new request.');
        }
        
        // Reload the entire job and quotes
        await loadJob();
      } else {
        throw new Error(data.message || 'Failed to respond');
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to respond to request');
      throw error;
    }
  };

  const isMyJob = job?.createdBy.id === user?.userId;
  const isAdmin = user?.role === 'admin';
  const canCloseContract = isMyJob || isAdmin;
  const isJobOpen = job?.status === 'open';

  useEffect(() => {
    setActiveTab(isMyJob ? 'quotes' : 'details');
  }, [isMyJob]);

  const handleEditJob = () => {
    navigation.navigate('EditJob', { jobId });
  };

  const handleShare = async () => {
    if (!job) return;
    
    await shareItem({
      id: jobId,
      title: job.title,
      type: 'contract',
      description: job.description,
      budget: job.budget,
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!job) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <EmptyState icon="alert-circle-outline" message="Job not found" />
      </SafeAreaView>
    );
  }

  const handleBack = () => {
    navigation.goBack();
  };

  const handleSendQuote = () => {
    navigation.navigate('SendQuote', { jobId, job });
  };

  const handleViewProfile = (userId: string) => {
    navigation.navigate('Profile', { userId });
  };

  const handleStartChat = (userId: string, userName: string) => {
    // Create a message with job details
    const jobType = job?.type === 'contract' ? 'Contract' : job?.type === 'supply' ? 'Supply Request' : 'Product';
    const budgetInfo = job?.budget ? `\nBudget: $${job.budget.toLocaleString()}` : '';
    const priceInfo = job?.price ? `\nPrice: $${job.price.toLocaleString()}` : '';
    
    const initialMessage = `Regarding ${jobType}: ${job?.title}${budgetInfo}${priceInfo}`;
    
    // Navigate to PersonalChat - conversation will be created when first message is sent
    navigation.navigate('PersonalChat', { 
      userId, 
      userName,
      initialMessage,
      // Don't pass conversationId - it will be created lazily on first message
    });
  };

  const handleAcceptQuote = async (quoteId: string, freelancerName: string) => {
    Alert.alert(
      'Accept Quote',
      `Are you sure you want to accept the quote from ${freelancerName}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Accept',
          onPress: async () => {
            try {
              await acceptQuote(quoteId);
              // Reload quotes to show updated status
              const quotesData = await fetchJobQuotes(jobId);
              setQuotes(quotesData);
              showSuccessToast('Quote accepted successfully!');
            } catch (error) {
              showErrorToast('Failed to accept quote. Please try again.');
            }
          },
        },
      ]
    );
  };

  const handleEditQuote = (quote: Quote) => {
    navigation.navigate('EditQuote', { quoteId: quote.id, quote });
  };

  const handleRequestCompletion = async (quoteId: string) => {
    Alert.alert(
      'Request Completion',
      'Are you sure you want to request completion? The other party will need to confirm before the contract is marked as completed.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Request',
          onPress: async () => {
            try {
              await requestCompletion(quoteId);
              // Reload quote
              await fetchMyQuotes();
              showSuccessToast('Completion requested! Waiting for confirmation from the other party.');
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to request completion.');
            }
          },
        },
      ]
    );
  };

  const handleConfirmCompletion = async (quoteId: string) => {
    Alert.alert(
      'Confirm Completion',
      'Are you sure you want to confirm completion? This will mark the contract as completed.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            try {
              await confirmCompletion(quoteId);
              // Reload quote
              await fetchMyQuotes();
              showSuccessToast('Contract completed successfully!');
            } catch (error: any) {
              showErrorToast(error.message || 'Failed to confirm completion.');
            }
          },
        },
      ]
    );
  };

  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  const formatDateDisplay = (value?: string | Date | null) => {
    if (!value) return 'Not specified';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return 'Not specified';
    return date.toLocaleDateString();
  };

  const formatExperience = (years?: number | null) => {
    if (years === undefined || years === null) return 'Not specified';
    if (years <= 0) return 'Entry level';
    return `${years}+ years`;
  };

  const getTabLabel = (tab: 'quotes' | 'details') => {
    if (tab === 'quotes') {
      return isMyJob ? 'Quotations' : 'Send Quote';
    }
    return 'Details';
  };

  const renderDetailsView = () => {
    return (
      <View style={styles.detailsContainer}>
        {/* Project Details Section */}
        <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.infoSectionHeader}>
            <Ionicons name="briefcase-outline" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>Project Details</ThemedText>
          </View>
          
          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Project Level</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job!.projectLevel || 'Not specified'}</ThemedText>
          </View>

          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Duration</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job!.duration || 'Not specified'}</ThemedText>
          </View>

          <View style={[styles.infoRow, { borderBottomColor: 'transparent' }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Contract Type</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job!.contractType || 'Not specified'}</ThemedText>
          </View>
        </View>

        {/* Location & Requirements */}
        <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.infoSectionHeader}>
            <Ionicons name="navigate-outline" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>Location & Requirements</ThemedText>
          </View>
          
          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Location Type</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job!.locationType || 'Not specified'}</ThemedText>
          </View>

          {job!.workAddress ? (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Work Address</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>{job!.workAddress}</ThemedText>
            </View>
          ) : (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Work Address</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.textSecondary }]}>Not specified</ThemedText>
            </View>
          )}

          {(job as any).state && (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>State</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>{(job as any).state}</ThemedText>
            </View>
          )}

          {(job as any).district && (
            <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>District</ThemedText>
              <ThemedText style={[styles.infoValue, { color: theme.text }]}>{(job as any).district}</ThemedText>
            </View>
          )}

          <View style={[styles.infoRow, { borderBottomColor: 'transparent' }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Experience</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{formatExperience(job!.experienceYears)}</ThemedText>
          </View>
        </View>

        {/* Project Timeline */}
        <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <View style={styles.infoSectionHeader}>
            <Ionicons name="calendar-outline" size={20} color={theme.accent} />
            <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>Project Timeline</ThemedText>
          </View>
          
          <View style={[styles.infoRow, { borderBottomColor: theme.border }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Expiry Date</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{formatDateDisplay(job!.projectExpiryDate)}</ThemedText>
          </View>

          <View style={[styles.infoRow, { borderBottomColor: 'transparent' }]}>
            <ThemedText style={[styles.infoLabel, { color: theme.textSecondary }]}>Deadline</ThemedText>
            <ThemedText style={[styles.infoValue, { color: theme.text }]}>{formatDateDisplay(job!.projectDeadlineDate)}</ThemedText>
          </View>
        </View>

        {/* Contract Document */}
        {job!.contractPdfUrl && (
          <View style={[styles.infoSection, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <View style={styles.infoSectionHeader}>
              <Ionicons name="document-text-outline" size={20} color={theme.accent} />
              <ThemedText style={[styles.infoSectionTitle, { color: theme.text }]}>Contract Document</ThemedText>
            </View>
            
            <TouchableOpacity
              style={[styles.pdfDownloadButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
              onPress={() => {
                if (job!.contractPdfUrl) {
                  Linking.openURL(job!.contractPdfUrl).catch(error => {
                    console.error('Failed to open PDF:', error);
                    showErrorToast('Failed to open PDF');
                  });
                }
              }}
            >
              <View style={styles.pdfInfo}>
                <Ionicons name="document-text" size={40} color={theme.accent} />
                <View style={styles.pdfDetails}>
                  <ThemedText style={[styles.pdfName, { color: theme.text }]} numberOfLines={1}>
                    {job!.contractPdfName || 'contract.pdf'}
                  </ThemedText>
                  {job!.contractPdfSize && (
                    <ThemedText style={[styles.pdfSize, { color: theme.textSecondary }]}>
                      {(job!.contractPdfSize / 1024 / 1024).toFixed(2)} MB
                    </ThemedText>
                  )}
                </View>
              </View>
              <Ionicons name="arrow-forward" size={24} color={theme.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Owner Actions */}
        {canCloseContract && isJobOpen && (
          <View style={styles.ownerActionsSection}>
            <TouchableOpacity
              style={[styles.closeJobButton, { backgroundColor: theme.error }]}
              onPress={() => {
                Alert.alert(
                  'Close Contract',
                  'Are you sure you want to close this contract? You can still view it but won\'t receive new quotations.',
                  [
                    { text: 'Cancel', style: 'cancel' },
                    {
                      text: 'Close Contract',
                      style: 'destructive',
                      onPress: async () => {
                        try {
                          // Update job status to completed or closed
                          Alert.alert('Success', 'Contract closed successfully');
                          navigation.goBack();
                        } catch (error) {
                          Alert.alert('Error', 'Failed to close contract');
                        }
                      },
                    },
                  ]
                );
              }}
            >
              <Ionicons name="close-circle" size={20} color="#FFFFFF" />
              <ThemedText style={styles.closeJobButtonText}>Close This Contract</ThemedText>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  const renderFreelancerView = () => {
    // Show quote if user has one, regardless of job status
    if (myQuote) {
      const pendingRequest = pendingCompletionRequests[myQuote.id];
      const hasPendingRequest = !!pendingRequest;
      const isRequester = pendingRequest?.requestedById === user?.userId;
      
      return (
        <View style={styles.actionSection}>
          <QuoteCard
            quote={myQuote}
            jobTitle={job!.title}
            currentUserId={user?.userId}
            onPress={() => {}}
            showFreelancerInfo={false}
            showChatButton={true}
            onChatPress={() => handleStartChat(job!.createdBy.id, job!.createdBy.name || 'Client')}
            showEditButton={myQuote.status === 'sent'}
            onEditPress={() => handleEditQuote(myQuote)}
            showEndContractButton={myQuote.status === 'accepted' && !hasPendingRequest}
            onEndContractPress={() => {
              setSelectedQuoteForEndContract(myQuote);
              setEndContractMode('request');
              setEndContractModalVisible(true);
            }}
            showRespondEndContractButton={hasPendingRequest && !isRequester}
            onRespondEndContractPress={() => {
              setSelectedQuoteForEndContract(myQuote);
              setEndContractMode('respond');
              setEndContractModalVisible(true);
            }}
            pendingCompletionRequest={pendingRequest}
          />
        </View>
      );
    }

    // Only show "Send Quote" button if job is still open
    if (!isJobOpen) {
      return null;
    }

    return (
      <View style={styles.actionSection}>
        <TouchableOpacity style={[styles.quoteButton, { backgroundColor: theme.accent }]} onPress={handleSendQuote}>
          <ThemedText style={[styles.quoteButtonText, { color: theme.textInverse }]}>Send a Quote</ThemedText>
        </TouchableOpacity>
      </View>
    );
  };

  const renderClientView = () => {
    if (loadingQuotes) {
      return (
        <View style={styles.quotesLoading}>
          <ActivityIndicator size="small" color={theme.accent} />
        </View>
      );
    }

    if (quotes.length === 0) {
      return (
        <View style={styles.quotesSection}>
          <EmptyState icon="document-text-outline" message="No quotes received yet" />
        </View>
      );
    }

    return (
      <View style={styles.quotesSection}>
        {quotes.map((quote) => {
          const pendingRequest = pendingCompletionRequests[quote.id];
          const hasPendingRequest = !!pendingRequest;
          const isRequester = pendingRequest?.requestedById === user?.userId;
          
          return (
            <View key={quote.id} style={styles.quoteCardWrapper}>
              <QuoteCard
                quote={quote}
                jobTitle={job!.title}
                currentUserId={user?.userId}
                onPress={() => {}}
                showFreelancerInfo={true}
                onProfilePress={() => handleViewProfile(quote.freelancer?.id || '')}
                showChatButton={true}
                onChatPress={() => handleStartChat(quote.freelancer?.id || '', quote.freelancer?.name || 'User')}
                showAcceptButton={quote.status === 'sent'}
                onAcceptPress={() => handleAcceptQuote(quote.id, quote.freelancer?.name || 'User')}
                showEndContractButton={quote.status === 'accepted' && !hasPendingRequest}
                onEndContractPress={() => {
                  setSelectedQuoteForEndContract(quote);
                  setEndContractMode('request');
                  setEndContractModalVisible(true);
                }}
                showRespondEndContractButton={hasPendingRequest && !isRequester}
                onRespondEndContractPress={() => {
                  setSelectedQuoteForEndContract(quote);
                  setEndContractMode('respond');
                  setEndContractModalVisible(true);
                }}
                pendingCompletionRequest={pendingRequest}
              />
            </View>
          );
        })}
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Contract Details</ThemedText>
        <View style={styles.headerActions}>
          {isMyJob && (
            <TouchableOpacity onPress={handleEditJob} style={styles.headerActionButton}>
              <Ionicons name="create-outline" size={24} color={theme.accent} />
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleShare} style={styles.headerActionButton}>
            <Ionicons name="share-outline" size={24} color={theme.text} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={[styles.jobInfo, { backgroundColor: theme.backgroundSecondary }]}>
          <View style={styles.titleRow}>
            <ThemedText style={[styles.jobTitle, { color: theme.text }]}>{job.title}</ThemedText>
          </View>

          <View style={styles.metaRow}>
            <TouchableOpacity 
              style={styles.clientInfo}
              onPress={() => handleViewProfile(job.createdBy.id)}
              activeOpacity={0.7}
            >
              {job.createdBy.photo ? (
                <Image 
                  source={{ uri: job.createdBy.photo }} 
                  style={styles.clientPhoto}
                />
              ) : (
                <Ionicons name="person-circle-outline" size={40} color={theme.textSecondary} />
              )}
              <View style={styles.clientText}>
                <ThemedText style={[styles.clientLabel, { color: theme.textSecondary }]}>Posted by</ThemedText>
                <ThemedText style={[styles.clientName, { color: theme.text }]}>{job.createdBy.name || 'Unknown'}</ThemedText>
              </View>
            </TouchableOpacity>
            <ThemedText style={[styles.time, { color: theme.textSecondary }]}>{getRelativeTime(job.createdAt)}</ThemedText>
          </View>

          <View style={[styles.detailCard, { backgroundColor: theme.background, borderColor: theme.border }]}>
            {job.type === 'contract' && (
              <>
                <View style={styles.detailItem}>
                  <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>Budget</ThemedText>
                  <ThemedText
                    style={[
                      styles.detailValue,
                      { color: job.budget ? theme.accent : theme.textSecondary },
                    ]}
                  >
                    {job.budget ? `$${job.budget.toLocaleString()}` : 'Not specified'}
                  </ThemedText>
                </View>
                <View style={[styles.detailDivider, { backgroundColor: theme.border }]} />
              </>
            )}
            {job.type === 'product' && job.price && (
              <>
                <View style={styles.detailItem}>
                  <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>Price</ThemedText>
                  <ThemedText style={[styles.detailValue, { color: theme.accent }]}>
                    ${job.price.toLocaleString()}
                  </ThemedText>
                </View>
                <View style={[styles.detailDivider, { backgroundColor: theme.border }]} />
              </>
            )}
            <View style={styles.detailItem}>
              <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>Category</ThemedText>
              <ThemedText style={[styles.detailValue, { color: theme.text }]}>{job.category}</ThemedText>
            </View>
            <View style={[styles.detailDivider, { backgroundColor: theme.border }]} />
            <View style={styles.detailItem}>
              <ThemedText style={[styles.detailLabel, { color: theme.textSecondary }]}>Status</ThemedText>
              <View style={[styles.statusPill, { backgroundColor: isJobOpen ? theme.success : theme.disabled }]}>
                <ThemedText style={[styles.statusPillText, { color: theme.textInverse }]}>{isJobOpen ? 'Open' : 'Closed'}</ThemedText>
              </View>
            </View>
          </View>

          <View style={styles.descriptionSection}>
            <ThemedText style={[styles.descriptionTitle, { color: theme.text }]}>Description</ThemedText>
            <ThemedText style={[styles.descriptionText, { color: theme.textSecondary }]}>{job.description}</ThemedText>
          </View>
        </View>

        {/* Tab Switcher */}
        {job.type === 'contract' && (
          <View style={[styles.tabSwitcher, { borderBottomColor: theme.border }]}>
            {(['quotes', 'details'] as const).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <TouchableOpacity
                  key={tab}
                  style={styles.tabButton}
                  onPress={() => setActiveTab(tab)}
                >
                  <ThemedText
                    style={[
                      styles.tabButtonText,
                      { color: isActive ? theme.accent : theme.textSecondary },
                    ]}
                  >
                    {getTabLabel(tab)}
                  </ThemedText>
                  {isActive && (
                    <View style={[styles.activeIndicator, { backgroundColor: theme.accent }]} />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        {/* Tab Content */}
        {activeTab === 'quotes' && (isMyJob ? renderClientView() : renderFreelancerView())}
        {activeTab === 'details' && job.type === 'contract' && renderDetailsView()}
      </ScrollView>
      
      {/* End Contract Modal */}
      <EndContractModal
        visible={endContractModalVisible}
        onClose={() => setEndContractModalVisible(false)}
        onSubmit={async (statusOrAction) => {
          if (endContractMode === 'request') {
            await handleEndContractRequest(statusOrAction as 'completed' | 'partially_completed' | 'cancelled');
          } else {
            await handleRespondToEndContract(statusOrAction as 'accept' | 'reject');
          }
        }}
        mode={endContractMode}
        requestedStatus={pendingCompletionRequests[selectedQuoteForEndContract?.id || '']?.requestedStatus as any}
        jobTitle={job.title}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 4,
  },
  headerActionButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  jobInfo: {
    padding: 16,
    marginBottom: 12,
  },
  titleRow: {
    marginBottom: 16,
  },
  jobTitle: {
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    marginRight: 12,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  clientInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clientPhoto: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  clientText: {
    marginLeft: 12,
  },
  clientLabel: {
    fontSize: 12,
  },
  clientName: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 2,
  },
  time: {
    fontSize: 12,
  },
  detailCard: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 16,
    alignItems: 'stretch',
  },
  detailItem: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    textAlign: 'center',
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  detailDivider: {
    width: 1,
    alignSelf: 'stretch',
    opacity: 0.4,
  },
  statusPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    alignSelf: 'center',
  },
  statusPillText: {
    fontSize: 13,
    fontWeight: '600',
  },
  descriptionSection: {
    marginTop: 8,
  },
  descriptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 15,
    lineHeight: 22,
  },
  detailsContainer: {
    padding: 16,
  },
  infoSection: {
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
  },
  infoSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  infoSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  infoLabel: {
    fontSize: 13,
    fontWeight: '600',
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'right',
    flexShrink: 1,
    marginLeft: 16,
  },
  addressContainer: {
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  addressText: {
    marginTop: 6,
    fontSize: 15,
    lineHeight: 20,
  },
  actionSection: {
    padding: 16,
  },
  quoteButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  quoteButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  quotesSection: {
    paddingVertical: 16,
  },
  quotesLoading: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  quoteCardWrapper: {
    marginBottom: 16,
  },
  quoteSentCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  quoteSentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  quoteSentTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  quoteSentDetails: {
    gap: 12,
  },
  quoteSentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quoteSentLabel: {
    fontSize: 14,
  },
  quoteSentAmount: {
    fontSize: 20,
    fontWeight: '700',
  },
  quoteSentStatus: {
    fontSize: 14,
    fontWeight: '600',
  },
  quoteSentMessageBox: {
    gap: 4,
  },
  quoteSentMessageLabel: {
    fontSize: 12,
  },
  quoteSentMessage: {
    fontSize: 14,
    lineHeight: 20,
  },
  pdfDownloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  pdfInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  pdfDetails: {
    flex: 1,
    marginLeft: 12,
  },
  pdfName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  pdfSize: {
    fontSize: 14,
  },
  tabSwitcher: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginTop: 0,
    marginBottom: 0,
    borderBottomWidth: 1,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    position: 'relative',
  },
  activeIndicator: {
    position: 'absolute',
    bottom: -1,
    left: '10%',
    right: '10%',
    height: 3,
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
  },
  tabButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  ownerActionsSection: {
    padding: 16,
    paddingTop: 8,
  },
  closeJobButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  closeJobButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ContractDetailScreen;
